<template>
  <div class="s-setting-container no-border">
    <div class="d-flex flex-row just-end">
      <SaveCancelSection
        v-if="adminRole === 'admin'"
        :cancel="resetPartnerConfig"
        :save="updatePartnerConfig"
        :disabled="!changeCheck"
      />
    </div>
    <div class="border-container">
      <ServiceRequestServicesSection
        :handleRequest="handleRequest"
        :changeList="updateOfferingList"
        :errorObj="errorObj"
        :partnerSettings="partnerSettings"
        :serviceCategoryList="serviceCategoryList"
        :serviceOffered="serviceOffered"
        :primaryContact="primaryContact"
      />
    </div>
    <ProcessOverlay :show="updating" text="Saving" />
    <div class="border-container flex-row just-between">
      <div class="flex-column">
        <div
          class="flex-row just-between mb-5 align-center"
          style="width: 100%"
        >
          <div class="flex-column">
            <div class="header">
              Service Contact Options
            </div>
            <div class="sub-header">
              Set and edit contacts below
            </div>
          </div>
        </div>
        <div class="mt-4">
          <SACRadio
            name="handle-request"
            field="handleRequest"
            value="smac"
            :checked="handleRequest === 'smac'"
            :change="handleChange"
            label="allow members to submit requests through the app"
            :disable="adminRole !== 'admin'"
            :error="errorObj.requestHandlerErr"
          />
        </div>
        <div class="mt-2">
          <SACRadio
            name="handle-request"
            field="handleRequest"
            value="self"
            :checked="handleRequest !== 'smac'"
            :change="handleChange"
            label="direct them to contact my company"
            :error="errorObj.websiteOrPhoneErr"
            :disable="adminRole !== 'admin'"
          />
        </div>
        <div class="sac__font-16 tomato mt-6">
          {{ errorObj.contactsErr }}
        </div>
        <div class="flex-column mt-2">
          <div v-for="service in services" style="width: fit-content">
            <div
              v-if="service !== undefined"
              class="req-card border-container flex-row just-between mt-4"
            >
              <div
                v-if="serviceContact(service) !== undefined"
                class="flex-column"
              >
                <div class="sac__font-18 semi-bold">
                  {{ service }} Member Contact
                </div>
                <div class="ac-text-header mt-6">
                  {{
                    `${serviceContact(service).firstName} ${
                      serviceContact(service).lastName
                    }`
                  }}
                </div>
                <div v-if="serviceContact(service).email" class="sac__font-16">
                  {{ serviceContact(service).email }}
                </div>
                <div class="sac__font-16">
                  {{ serviceContact(service).phone }}
                </div>
                <div class="ac-text-body">
                  {{ serviceContact(service).title }}
                </div>
                <div class="ac-text-body">
                  {{ serviceContact(service).websiteLink }}
                </div>
                <div class="sac__font-16 n40 mt-6">
                  Copies of requests will also be emailed to:
                </div>
                <div
                  v-if="
                    serviceContact(service)?.emailCopy
                      && JSON.parse(serviceContact(service).emailCopy).length > 0
                  "
                  class="sac__font-16 n40"
                >
                  <div
                    v-for="email in JSON.parse(
                      serviceContact(service).emailCopy,
                    )"
                  >
                    {{ email }}
                  </div>
                </div>
                <div v-else class="sac__font-16 n40">
                  None (edit contact to manage)
                </div>
              </div>
              <div v-else class="flex-column">
                <div class="sac__font-18 semi-bold">
                  {{ service }} Member Contact
                </div>
                <div class="sac__font-16 mt-8">
                  None provided. Add information to save
                </div>
              </div>
              <div
                v-if="
                  adminRole === 'admin' && serviceContact(service) !== undefined
                "
                class="ac-row-container"
              >
                <button
                  type="button"
                  @click="
                    toggleActionModal(
                      true,
                      'edit-contact',
                      serviceContact(service),
                    )
                  "
                >
                  <v-icon color="#4471F1" medium>
                    mdi-pencil-outline
                  </v-icon>
                </button>
              </div>
              <div v-else-if="adminRole === 'admin'" class="ac-row-container">
                <button
                  type="button"
                  @click="
                    toggleActionModal(true, 'edit-contact', {
                      contactType: 'ServiceReq',
                      category: service,
                    })
                  "
                >
                  <v-icon color="#4979FF" dark medium>
                    mdi-plus
                  </v-icon>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex-row">
        <div v-if="handleRequest === 'smac'" class="flex-row">
          <ServiceRequestPreview
            :phone="primaryContact?.phone ?? supportPhone"
            method="phone"
            :email="primaryContact?.email ?? supportEmail"
            :website="primaryContact?.websiteLink"
            :primaryColor="primaryColor"
            :companyName="companyName"
            :logoPreview="logoPreview"
            image="hvac101"
            :button="true"
          />
          <div class="pc-preview-container">
            <div class="pc-preview-background">
              <img
                alt="phone-empty"
                src="@shared/assets/img/setting/request-phone.png"
                class="pc-phone-img"
              />
            </div>
            <div class="pc-preview-title">
              Requesting Service
            </div>
            <div class="sac__font-14 n60 text-center">
              Member App
            </div>
          </div>
        </div>
        <div v-else class="flex-row">
          <ServiceRequestPreview
            :phone="primaryContact?.phone ?? supportPhone"
            method="phone"
            :email="primaryContact?.email ?? supportEmail"
            :website="primaryContact?.websiteLink"
            :primaryColor="primaryColor"
            :companyName="companyName"
            :logoPreview="logoPreview"
            image="hvac101"
          />
          <ServiceRequestPreview
            :phone="primaryContact?.phone ?? supportPhone"
            method="phone"
            :serviceRequest="true"
            :email="primaryContact?.email ?? supportEmail"
            :website="primaryContact?.websiteLink "
            :primaryColor="primaryColor"
            :companyName="companyName"
            :logoPreview="logoPreview"
          />
        </div>
      </div>
    </div>
    <ContactModal
      :show="showModal"
      :view="modalView"
      :toggle="toggleActionModal"
      :contact="contact"
      :currentCorporation="currentCorporation"
      :update="update"
    />
  </div>
</template>

<script>
import SACRadio from '@shared/components/sac/SACRadio.vue';
import MyTechPreview from '@shared/components/partnerSettings/serviceRequest/MyTechPreview.vue';
import SaveCancelSection from '../shared/SaveCancelSection.vue';
import phoneValidation from '../../../lib/errorCatch/phoneValidation';
import urlValidation from '../../../lib/errorCatch/urlValidation';
import formatPhoneNumber from '../../../lib/formatter/formatPhoneNumber';
import ProcessOverlay from '../../ProcessOverlay.vue';
import ServiceRequestServicesSection from '../../partnerSettings/ServiceRequestServicesSection.vue';
import partnerSettingsMixin from '../../../mixins/partnerSettingsMixin';
import PartnerContact from './PartnerContact.vue';
import ContactModal from '../../partnerSettings/contactModal/ContactModal.vue';
import ServiceRequestPreview from '../../partnerSettings/branding/ServiceRequestPreview.vue';

export default {
  name: 'ServiceRequestSection',
  components: {
    ServiceRequestServicesSection,
    ProcessOverlay,
    SaveCancelSection,
    PartnerContact,
    ContactModal,
    ServiceRequestPreview,
    SACRadio,
    MyTechPreview,
  },
  mixins: [partnerSettingsMixin],
  props: {
    partnerSettings: Object,
    serviceCategoryList: Array,
    currentCorporation: Object,
    update: Function,
  },
  data() {
    const {
      SERVICE_REQUEST_HANDLE_REQUEST = 'smac',
      SERVICES_OFFERED = '',
      PRIMARY_COLOR,
      LOGO_URL,
    } = this.partnerSettings;
    return {
      logoPreview: LOGO_URL,
      companyName: this.currentCorporation.displayName,
      primaryColor: PRIMARY_COLOR,
      handleRequest: SERVICE_REQUEST_HANDLE_REQUEST,
      errorObj: {},
      updating: false,
      serviceOffered: SERVICES_OFFERED.split(','),
      showModal: false,
      contact: {},
      modalView: '',
    };
  },
  computed: {
    changeCheck() {
      const {
        SERVICE_REQUEST_HANDLE_REQUEST = 'smac',
        SERVICES_OFFERED = '',
      } = this.partnerSettings;
      let changes = false;
      const servicesOffered = this.formServicesOffered();
      if (SERVICES_OFFERED !== servicesOffered) {
        changes = true;
      }
      if (SERVICE_REQUEST_HANDLE_REQUEST !== this.handleRequest) {
        changes = true;
      }
      if (!changes) {
        this.changePartnerSettingsValue('showWarningOnExit', false);
      } else if (changes && !this.showWarningOnExit && !this.warningTab) {
        this.changePartnerSettingsValue('showWarningOnExit', true);
      }
      return changes;
    },
    memberContacts() {
      return this.contacts.filter((x) => x.contactType === 'ServiceReq');
    },
    supportContact() {
      return this.contacts.find((x) => x.contactType === 'Support');
    },
    primaryContact() {
      return (
        this.memberContacts.find((x) => x.category === 'HVAC') ?? this.memberContacts[0] ?? this.supportContact
      );
    },
    services() {
      return this.serviceOffered;
    },
  },
  async mounted() {
    this.loadingPlans = true;
    await this.$store.dispatch('getPartnerContacts', {
      corpId: this.currentCorporation.id,
    });
    this.loadingPlans = false;
  },
  methods: {
    handleChange(field, value) {
      let v = value;
      if (field === 'phone') {
        v = formatPhoneNumber(v);
      }
      this[field] = v;
    },
    updateOfferingList(category) {
      if (this.serviceOffered.includes(category)) {
        this.serviceOffered = this.serviceOffered.filter((c) => c !== category);
      } else {
        this.serviceOffered = [...this.serviceOffered, category];
      }
    },
    resetPartnerConfig() {
      const {
        SERVICE_REQUEST_HANDLE_REQUEST = 'smac',
        SERVICES_OFFERED = '',
      } = this.partnerSettings;
      this.handleRequest = SERVICE_REQUEST_HANDLE_REQUEST;
      this.serviceOffered = SERVICES_OFFERED.split(',');
      this.errorObj = {};
    },
    checkForServiceChanges() {
      const { SERVICES_OFFERED: services, ST_SETUP_COMPLETE: isSetupComplete } = this.partnerSettings;
      if (isSetupComplete === 'false') {
        return false;
      }
      const existing = services?.split(',').map((s) => s.trim());
      const newOffering = this.serviceOffered;
      const diff = existing.filter(x => !newOffering.includes(x))
        .concat(newOffering.filter(x => !existing.includes(x)));
      return diff.length !== 0;
    },
    async updatePartnerConfig() {
      this.errorObj = this.errorCheck();
      const payload = this.updateHelper();
      if (this.checkForServiceChanges()) {
        this.toggleModal(true, 'service-changes-warning', payload);
      } else {
        if (Object.keys(this.errorObj).length === 0) {
          this.changePartnerSettingsValue('showWarningOnExit', false);
          this.updating = true;
          await this.update(payload);
          this.updating = false;
        }
      }
    },
    errorCheck() {
      const errorObj = {};
      const {
        handleRequest,
        website,
        phone,
      } = this;
      if (handleRequest === 'website') {
        if (!website) {
          errorObj.websiteErr = 'Website is required.';
        } else if (!urlValidation(website)) {
          errorObj.websiteErr = 'Website URL is not valid.';
        }
      }
      if (handleRequest === 'phone') {
        if (!phone) {
          errorObj.phoneErr = 'Phone number is required.';
        } else if (!phoneValidation(phone)) {
          errorObj.phoneErr = 'Phone number is not valid.';
        }
      }
      if (this.memberContacts.length === 0) {
        errorObj.contactsErr = 'Save at least 1 contact.';
      }
      return errorObj;
    },
    formServicesOffered() {
      return this.serviceOffered.join(',');
    },
    updateHelper() {
      let {
        handleRequest,
      } = this;
      const servicesOffered = this.formServicesOffered();
      return {
        SERVICE_REQUEST_HANDLE_REQUEST: handleRequest,
        SERVICES_OFFERED: servicesOffered,
      };
    },
    toggleActionModal(show, view, contact) {
      this.showModal = show;
      if (show) {
        this.modalView = view;
        this.contact = contact;
      } else {
        this.contact = {};
        this.type = '';
      }
    },
    serviceContact(serviceName) {
      return this.memberContacts.find((x) => x.category === serviceName);
    },
  },
};
</script>

<style lang="scss" scoped>
.border-container {
  border: 1px solid #c4c5cc;
  padding: 12px 18px;
}

.header {
  font-size: 20px;
  font-weight: 700;
}

.cards-wrapper {
  flex-wrap: wrap;
  display: flex;
}

.sub-header {
  font-size: 16px;
  font-weight: 500;
  color: $N60;
}

.sr-divider {
  margin-top: 20px;
  border-top: 2px solid #d0d3da;
}

.req-card {
  width: 500px;
  border-radius: 4px;
}

.pc-preview-container {
  margin-left: 24px;
  height: fit-content;

  .pc-preview-background {
    position: relative;
    border-radius: 50px;
    height: 91%;
  }

  .pc-phone-img {
    height: 540px;
  }

  .pc-phone-item {
    height: 50px;
    padding: 10px;
  }

  .pc-absolute-overlay {
    position: absolute;
    left: 15px;
    right: 15px;
    top: 80px;
    height: 400px;
    padding: 6px;

    .pc-bubble {
      background-color: #2b3757;
      width: 100%;
      height: 48px;
      border-radius: 4px;

      &:not(:first-child) {
        margin-top: 4px;
      }

      .pc-phone-container {
        display: flex;
        align-items: flex-start;
        flex-direction: column;
        padding-top: 8px;

        .pc-phone-title {
          font-size: 12px;
          font-weight: bold;
          color: white;
        }

        .pc-phone-subtitle {
          font-size: 8px;
          color: white;
        }
      }
    }
  }

  .pc-preview-title {
    margin-top: 14px;
    text-align: center;
  }
}
</style>
